import {
  Anchor as AnchorIcon,
  Briefcase,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Edit3,
  ExternalLink,
  FileText,
  Filter,
  Globe,
  ListTodo,
  Mail,
  MapPin,
  MessageSquare,
  MoreHorizontal,
  Paperclip,
  Plus,
  Sparkles,
  StickyNote,
  Tag as TagIcon,
  User,
  X,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { mapSchemaToFields } from '../../custom-fields/utils';
import type { AppKey } from '../../lib/app-registry';
import { getAppPersona } from '../../lib/feature-flags';
import { toggleTaskStatusKey } from '../../lib/task-list';
import { type RouterOutputs, trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Switch } from '../../ui/switch';
import { ContactCommentThread } from '../comments/ContactCommentThread';
import { EntityFeatureBar, type EntityFeatureBarItem } from '../features/EntityFeatureBar';
import { LinkedJobsSection } from '../job-plan/LinkedJobsSection';
import { NotesPanel } from '../notes/NotesPanel';
import { RichTextContent } from '../rich-text-content';
import { TaskPanel } from '../tasks/TaskPanel';
import { AddressBlock } from './AddressBlock';
import { ContactCustomFields } from './ContactCustomFields';
import { ContactFilesTab } from './ContactFilesTab';
import { TagChips } from './TagChips';

export type ContactDetail = NonNullable<RouterOutputs['contacts']['getById']>;

export interface ActionCardItem {
  id: string;
  title: string;
  description: string;
  estimatedTime: string;
  iconType: 'email' | 'meeting' | 'file';
  primaryActionLabel: string;
  secondaryActionLabel: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

export interface ActivityFeedItem {
  id: string;
  title: string;
  timestamp: string;
  category: 'email' | 'meeting' | 'note' | 'file' | 'job' | 'task';
  preview: string;
  details?: string;
  author?: string;
  hasMore?: boolean;
}

export interface ThreadMessageItem {
  id: string;
  authorName: string;
  authorAvatar?: string;
  timestamp: string;
  content: string;
  likesCount?: number;
}

export interface SharedContactPageProps {
  contact?: ContactDetail | null;
  isLoading?: boolean;
  appKey?: AppKey;
  onEditContact?: () => void;
  onSendEmail?: (email?: string) => void;
  /**
   * Overrides "Add Task". Omit it and the page opens its own create-task dialog pre-linked to
   * this contact — which is what every task surface should do. Job Plan passes this to route
   * to its job-creation flow instead, since a job is what its contacts actually get.
   */
  onAddTask?: () => void;
  onAddAnchor?: () => void;
  onClose?: () => void;
  isReadOnly?: boolean;
  actionCards?: ActionCardItem[];
  activityItems?: ActivityFeedItem[];
}

// ─── Default Mock Data matching design specs ──────────────────────────────────
const DEFAULT_ACTION_CARDS: ActionCardItem[] = [
  {
    id: 'action-1',
    title: 'Send Follow-up Email to Contact',
    description:
      "It's been over a week since the Q3 Proposal was sent. A friendly check-in is recommended.",
    estimatedTime: 'Approx. 5 mins',
    iconType: 'email',
    primaryActionLabel: 'Send Now',
    secondaryActionLabel: 'Dismiss Suggestion',
  },
  {
    id: 'action-2',
    title: 'Schedule Budget Review Meeting',
    description: 'Discussed budget requirements on July 19. Ready to lock in Q4 allocation.',
    estimatedTime: 'Approx. 15 mins',
    iconType: 'meeting',
    primaryActionLabel: 'Schedule Meeting',
    secondaryActionLabel: 'Snooze',
  },
  {
    id: 'action-3',
    title: 'Review Contract Document',
    description: 'Pending contract document received from client legal team.',
    estimatedTime: 'Approx. 2 mins',
    iconType: 'file',
    primaryActionLabel: 'Review Document',
    secondaryActionLabel: 'Dismiss',
  },
];

const DEFAULT_ACTIVITY_FEED: ActivityFeedItem[] = [
  {
    id: 'act-1',
    title: 'Email Sent: Q3 Proposal',
    timestamp: 'Today, 10:30 AM',
    category: 'email',
    preview:
      'Hi, following up on our recent call regarding the Q3 workspace deployment and timeline updates.',
    hasMore: true,
  },
  {
    id: 'act-2',
    title: 'Meeting Scheduled: Follow-up discussion',
    timestamp: 'Yesterday, 2:00 PM',
    category: 'meeting',
    preview: 'Meeting scheduled: follow-up discussion\nYesterday, July 19th - 2:00 PM',
  },
  {
    id: 'act-3',
    title: 'Note Added: Discussed budget requirements',
    timestamp: 'July 19, 4:15 PM',
    category: 'note',
    preview: 'Note added: Discussed budget requirements and seat allocations.',
  },
];

const DEFAULT_THREAD_MESSAGES: ThreadMessageItem[] = [
  {
    id: 'msg-1',
    authorName: 'Sarah Jones',
    timestamp: '1:38 AM',
    content: 'Hi team, reviewing the workspace context and contract details.',
    likesCount: 1,
  },
  {
    id: 'msg-2',
    authorName: 'Acme Support',
    timestamp: '1:57 AM',
    content: 'Would you find the service contract profits token on the system?',
  },
  {
    id: 'msg-3',
    authorName: 'Sarah Jones',
    timestamp: '1:38 PM',
    content: 'Thanks for scheduling the discussion. 😊',
  },
  {
    id: 'msg-4',
    authorName: 'Acme Support',
    timestamp: '3:57 PM',
    content: 'Hey, I discussed the new updates in this thread.',
  },
];

export type ContactSubTab = 'tasks' | 'notes' | 'files' | 'comments' | 'anchors' | 'jobs' | 'meta';

export function SharedContactPage({
  contact,
  isLoading: _isLoading,
  appKey,
  onEditContact,
  onSendEmail,
  onAddTask,
  onAddAnchor,
  onClose,
  isReadOnly = false,
  actionCards = DEFAULT_ACTION_CARDS,
  activityItems = DEFAULT_ACTIVITY_FEED,
}: SharedContactPageProps) {
  // ── States ─────────────────────────────────────────────────────────────────
  const [activeActionIndex, setActiveActionIndex] = useState(0);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const [activeSubTab, setActiveSubTab] = useState<ContactSubTab>('tasks');
  const { data: featureFlags } = trpc.featureFlags.list.useQuery();
  const persona = getAppPersona(appKey);
  const contactTasksQuery = trpc.tasks.getByContact.useQuery(
    { contactId: contact?.id ?? 0 },
    { enabled: !!contact?.id }
  );

  const { data: contactNotes = [] } = trpc.notes.list.useQuery(
    { entityType: 'contact', entityId: contact?.id ?? 0 },
    { enabled: !!contact?.id && (featureFlags ? featureFlags['contact:notes'] : true) }
  );
  const notesCount = contactNotes.length;

  const { data: contactFiles = [] } = trpc.contacts.getFiles.useQuery(
    { contactId: contact?.id ?? 0 },
    { enabled: !!contact?.id && (featureFlags ? featureFlags['contact:files'] : true) }
  );
  const filesCount = contactFiles.length;

  const { data: contactThread } = trpc.threads.getThreadForContact.useQuery(
    { contactId: contact?.id ?? 0 },
    { enabled: !!contact?.id && (featureFlags ? featureFlags['contact:comments'] : true) }
  );
  const { data: contactCommentsData } = trpc.comments.getForThread.useQuery(
    { threadId: contactThread?.id ?? 0, limit: 50 },
    { enabled: !!contactThread?.id && (featureFlags ? featureFlags['contact:comments'] : true) }
  );
  const commentsCount = contactCommentsData?.items?.filter(Boolean).length ?? 0;

  const taskCount = contactTasksQuery.data?.length ?? 0;
  const anchorsCount = contact?.anchors?.length ?? 0;

  const { data: contactJobs = [] } = trpc.jobPlan.jobs.listForContact.useQuery(
    { contactId: contact?.id ?? 0 },
    { enabled: !!contact?.id && persona.supportedRelationTabs.includes('jobs') }
  );
  const jobsCount = contactJobs.length;

  const featureBarItems = useMemo((): EntityFeatureBarItem<ContactSubTab>[] => {
    const items: EntityFeatureBarItem<ContactSubTab>[] = [
      {
        value: 'tasks',
        title: 'Tasks',
        description: `${taskCount} tasks linked to this contact`,
        icon: ListTodo,
        badge: taskCount > 0 ? taskCount : undefined,
      },
    ];

    if (featureFlags ? featureFlags['contact:notes'] : true) {
      items.push({
        value: 'notes',
        title: 'Notes',
        description: `${notesCount} notes for this contact`,
        icon: StickyNote,
        badge: notesCount > 0 ? notesCount : undefined,
      });
    }

    if (featureFlags ? featureFlags['contact:files'] : true) {
      items.push({
        value: 'files',
        title: 'Files',
        description: `${filesCount} files attached to this contact`,
        icon: Paperclip,
        badge: filesCount > 0 ? filesCount : undefined,
      });
    }

    if (featureFlags ? featureFlags['contact:comments'] : true) {
      items.push({
        value: 'comments',
        title: 'Comments',
        description: `${commentsCount} team comments`,
        icon: MessageSquare,
        badge: commentsCount > 0 ? commentsCount : undefined,
      });
    }

    if (persona.supportedRelationTabs.includes('anchors')) {
      items.push({
        value: 'anchors',
        title: 'Anchors',
        description: `${anchorsCount} connected web anchors`,
        icon: AnchorIcon,
        badge: anchorsCount > 0 ? anchorsCount : undefined,
      });
    }

    if (persona.supportedRelationTabs.includes('jobs')) {
      items.push({
        value: 'jobs',
        title: 'Jobs',
        description: `${jobsCount} service jobs`,
        icon: Briefcase,
        badge: jobsCount > 0 ? jobsCount : undefined,
      });
    }

    items.push({
      value: 'meta',
      title: 'Meta',
      description: 'Contact properties & custom fields',
      icon: User,
    });

    return items;
  }, [
    taskCount,
    notesCount,
    filesCount,
    commentsCount,
    anchorsCount,
    jobsCount,
    featureFlags,
    persona,
  ]);

  const visibleTabs = useMemo(() => {
    return featureBarItems.map((item) => item.value);
  }, [featureBarItems]);

  const currentTab: ContactSubTab = visibleTabs.includes(activeSubTab)
    ? activeSubTab
    : (visibleTabs[0] ?? 'tasks');

  const allowedFeedCategories = useMemo(() => {
    return persona.activityCategories.filter((cat) => {
      if (cat === 'note') return featureFlags ? featureFlags['contact:notes'] : true;
      if (cat === 'file') return featureFlags ? featureFlags['contact:files'] : true;
      return true;
    });
  }, [persona, featureFlags]);

  // Activity Feed Filter Toggles
  const [filterJobs, setFilterJobs] = useState(true);
  const [filterEmails, setFilterEmails] = useState(true);
  const [filterNotes, setFilterNotes] = useState(true);
  const [filterFiles, setFilterFiles] = useState(true);
  const [filterMeetings, setFilterMeetings] = useState(true);

  // Thread Comments State
  const [_threadMessages, setThreadMessages] =
    useState<ThreadMessageItem[]>(DEFAULT_THREAD_MESSAGES);
  const [newCommentText, setNewCommentText] = useState('');

  // ── Resolved Display Values ─────────────────────────────────────────────────
  const displayName = contact?.displayName || contact?.label || 'Sarah Jones';
  const jobTitle = contact?.jobTitle || 'VP of Sales';
  const companyName = contact?.companyName || 'TechSolutions Inc.';
  const primaryEmail =
    contact?.emails?.find((e) => e.isPrimary)?.email ||
    contact?.emails?.[0]?.email ||
    'sarah@techsolutions.com';

  // Custom Fields Mapping
  const _customFields = React.useMemo(
    () => mapSchemaToFields(contact?.contactType?.customFieldsSchema),
    [contact?.contactType]
  );

  const handleNextAction = () => {
    setActiveActionIndex((prev) => (prev + 1) % actionCards.length);
  };

  const handlePrevAction = () => {
    setActiveActionIndex((prev) => (prev - 1 + actionCards.length) % actionCards.length);
  };

  const _handleSendComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCommentText.trim()) return;
    const msg: ThreadMessageItem = {
      id: `msg-${Date.now()}`,
      authorName: 'Me',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      content: newCommentText.trim(),
    };
    setThreadMessages((prev) => [...prev, msg]);
    setNewCommentText('');
  };

  const currentActionCard = actionCards[activeActionIndex] || actionCards[0];

  const { data: remoteFeed } = trpc.contacts.getActivityFeed.useQuery(
    { contactId: contact?.id ?? 0, allowedCategories: allowedFeedCategories },
    { enabled: !!contact?.id }
  );

  const utils = trpc.useUtils();
  const taskStatusesQuery = trpc.taskStatuses.list.useQuery();
  const toggleTaskMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      if (contact?.id) utils.tasks.getByContact.invalidate({ contactId: contact.id });
      utils.tasks.getAll.invalidate();
    },
  });
  const { data: workspaceMembers = [] } = trpc.workspace.listMembers.useQuery();
  /**
   * Every "Add Task" on this page routes through here, so the header button, the Tasks tab, and
   * the empty state cannot drift apart again. Without an override it opens `TaskPanel`'s create
   * dialog pre-linked to this contact — the two portals previously navigated to the unfiltered
   * task index instead, losing the contact and creating nothing.
   */
  const handleAddTask = () => {
    if (onAddTask) {
      onAddTask();
      return;
    }
    if (!contact?.id) return;
    // The panel owns creating a task, and it only mounts on the Tasks tab — so switch there and
    // open it, rather than this page keeping a second dialog that could drift from the panel's.
    setActiveSubTab('tasks');
    setIsCreateTaskOpen(true);
  };

  const effectiveActivityFeed: ActivityFeedItem[] = contact?.id
    ? (remoteFeed as ActivityFeedItem[]) || []
    : activityItems || [];

  const filteredFeed = effectiveActivityFeed.filter((item: ActivityFeedItem) => {
    if ((item.category === 'job' || item.category === 'task') && !filterJobs) return false;
    if (item.category === 'email' && !filterEmails) return false;
    if (item.category === 'note' && !filterNotes) return false;
    if (item.category === 'file' && !filterFiles) return false;
    if (item.category === 'meeting' && !filterMeetings) return false;
    return true;
  });

  return (
    <div className="flex flex-col lg:flex-row h-full w-full bg-background text-foreground overflow-hidden font-sans select-none">
      {/* ── MAIN CONTENT AREA (Header + Actions Carousel + Activity Feed) ───── */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden p-4 lg:p-6 space-y-5">
        {/* Top Header Row: Contact Profile Card (Left) & Actions Carousel (Middle/Right) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 shrink-0">
          {/* Top Left: Header / Hero Card */}
          <div className="md:col-span-6 lg:col-span-6 bg-card border border-border rounded-xl p-5 flex flex-col items-center text-center shadow-xs relative overflow-hidden">
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="absolute top-3 right-3 h-7 w-7 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-0.5 shadow-md shrink-0 mb-3">
              {contact?.avatarUrl ? (
                <img
                  src={contact.avatarUrl}
                  alt={displayName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full bg-slate-900 flex items-center justify-center text-2xl font-bold text-white uppercase">
                  {displayName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)}
                </div>
              )}
            </div>
            <h2 className="text-xl font-bold tracking-tight text-foreground">{displayName}</h2>
            {jobTitle && <p className="text-xs font-medium text-primary mt-0.5">{jobTitle}</p>}
            {companyName && (
              <p className="text-xs font-medium text-muted-foreground mt-0.5">{companyName}</p>
            )}

            {contact?.contactType?.label && (
              <Badge
                variant="outline"
                className="bg-primary/10 text-primary border-primary/20 mt-2"
              >
                {contact.contactType.label}
              </Badge>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2 mt-4 w-full justify-center flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={onEditContact}
                className="bg-secondary hover:bg-secondary/80 text-secondary-foreground border border-border rounded-xl text-xs px-3.5 h-8 gap-1.5 shadow-xs"
              >
                <Edit3 className="w-3.5 h-3.5 text-primary" />
                Edit Contact
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => onSendEmail?.(primaryEmail)}
                className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 rounded-xl text-xs px-3.5 h-8 gap-1.5 shadow-xs"
              >
                <Mail className="w-3.5 h-3.5 text-emerald-500" />
                Send Email
              </Button>

              {!isReadOnly && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddTask}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-xs px-3.5 h-8 gap-1.5 shadow-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Task
                </Button>
              )}
            </div>
          </div>

          {/* Top Middle: Actions Carousel Panel */}
          <div className="md:col-span-6 lg:col-span-6 bg-card border border-border rounded-xl p-5 flex flex-col justify-between shadow-xs relative">
            <div className="flex items-center justify-between mb-3 shrink-0">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20">
                  <Sparkles className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-semibold text-foreground tracking-wide">Actions</h3>
              </div>

              {/* Carousel Indicators & Controls */}
              {actionCards.length > 1 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-1 mr-2">
                    {actionCards.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveActionIndex(idx)}
                        className={`h-1.5 rounded-full transition-all ${
                          idx === activeActionIndex
                            ? 'w-4 bg-primary'
                            : 'w-1.5 bg-muted-foreground/30'
                        }`}
                        aria-label={`Go to slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevAction}
                    className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNextAction}
                    className="h-7 w-7 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* ActionCard Content */}
            {currentActionCard && (
              <div className="flex-1 flex flex-col justify-between bg-slate-50 dark:bg-slate-900/60 border border-border rounded-xl p-4 transition-all">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold text-foreground line-clamp-1">
                      {currentActionCard.title}
                    </h4>
                    <div className="p-1.5 rounded-lg bg-background border border-border text-foreground">
                      {currentActionCard.iconType === 'email' && (
                        <Mail className="w-3.5 h-3.5 text-primary" />
                      )}
                      {currentActionCard.iconType === 'meeting' && (
                        <Calendar className="w-3.5 h-3.5 text-sky-500" />
                      )}
                      {currentActionCard.iconType === 'file' && (
                        <FileText className="w-3.5 h-3.5 text-amber-500" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                    {currentActionCard.description}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-medium">
                    {currentActionCard.estimatedTime}
                  </p>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
                  <Button
                    size="sm"
                    onClick={currentActionCard.onPrimaryAction}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-xs px-3.5 h-7 font-medium shadow-xs"
                  >
                    {currentActionCard.primaryActionLabel}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={currentActionCard.onSecondaryAction}
                    className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-xl text-xs px-3.5 h-7"
                  >
                    {currentActionCard.secondaryActionLabel}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Left: Activity Feed */}
        <div className="flex-1 min-h-0 bg-card border border-border rounded-xl p-5 flex flex-col space-y-4 shadow-xs overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
            <h3 className="text-base font-bold text-foreground tracking-tight">Activity Feed</h3>

            {/* Filter Switches & Dropdown */}
            <div className="flex items-center gap-3 flex-wrap">
              {allowedFeedCategories.includes('job') && (
                <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-900/80 px-2.5 py-1 rounded-xl border border-border">
                  <Switch
                    checked={filterJobs}
                    onCheckedChange={setFilterJobs}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                  <span className="text-xs text-foreground font-medium">Jobs</span>
                </div>
              )}

              {allowedFeedCategories.includes('email') && (
                <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-900/80 px-2.5 py-1 rounded-xl border border-border">
                  <Switch
                    checked={filterEmails}
                    onCheckedChange={setFilterEmails}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                  <span className="text-xs text-foreground font-medium">Emails</span>
                </div>
              )}

              {allowedFeedCategories.includes('note') && (
                <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-900/80 px-2.5 py-1 rounded-xl border border-border">
                  <Switch
                    checked={filterNotes}
                    onCheckedChange={setFilterNotes}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                  <span className="text-xs text-foreground font-medium">Notes</span>
                </div>
              )}

              {allowedFeedCategories.includes('file') && (
                <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-900/80 px-2.5 py-1 rounded-xl border border-border">
                  <Switch
                    checked={filterFiles}
                    onCheckedChange={setFilterFiles}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                  <span className="text-xs text-foreground font-medium">Files</span>
                </div>
              )}

              {allowedFeedCategories.includes('meeting') && (
                <div className="flex items-center gap-1.5 bg-slate-100/80 dark:bg-slate-900/80 px-2.5 py-1 rounded-xl border border-border">
                  <Switch
                    checked={filterMeetings}
                    onCheckedChange={setFilterMeetings}
                    className="data-[state=checked]:bg-primary scale-75"
                  />
                  <span className="text-xs text-foreground font-medium">Meetings</span>
                </div>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-slate-100/80 dark:bg-slate-900/80 border-border text-foreground hover:bg-accent rounded-xl text-xs h-7 gap-1"
                  >
                    <Filter className="w-3 h-3" /> Filter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-popover border-border text-popover-foreground"
                >
                  <DropdownMenuItem className="focus:bg-accent focus:text-accent-foreground cursor-pointer text-xs">
                    Newest First
                  </DropdownMenuItem>
                  <DropdownMenuItem className="focus:bg-accent focus:text-accent-foreground cursor-pointer text-xs">
                    Oldest First
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Activity Feed Timeline */}
          <div className="flex-1 min-h-0 overflow-y-auto relative pl-6 pr-2 space-y-4 pt-1 scrollbar-thin before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
            {filteredFeed.length > 0 ? (
              filteredFeed.map((item: ActivityFeedItem) => (
                <div key={item.id} className="relative flex items-start gap-4 group">
                  {/* Timeline Icon Node */}
                  <div className="absolute -left-6 top-1 w-5 h-5 rounded-full bg-card border-2 border-primary flex items-center justify-center z-10 shadow-xs">
                    {(item.category === 'job' || item.category === 'task') && (
                      <Briefcase className="w-2.5 h-2.5 text-primary" />
                    )}
                    {item.category === 'email' && <Mail className="w-2.5 h-2.5 text-blue-500" />}
                    {item.category === 'meeting' && (
                      <Calendar className="w-2.5 h-2.5 text-purple-500" />
                    )}
                    {item.category === 'note' && (
                      <StickyNote className="w-2.5 h-2.5 text-amber-500" />
                    )}
                    {item.category === 'file' && (
                      <FileText className="w-2.5 h-2.5 text-emerald-500" />
                    )}
                  </div>

                  {/* Feed Card */}
                  <div className="flex-1 bg-slate-50 dark:bg-slate-900/60 border border-border hover:border-primary/40 rounded-xl p-4 transition-all shadow-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-foreground">{item.title}</h4>
                        <p className="text-[11px] text-muted-foreground font-medium">
                          {item.timestamp}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg"
                        >
                          <MoreHorizontal className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                      {item.preview}
                    </p>

                    {item.hasMore && (
                      <button className="text-[11px] text-primary hover:underline font-medium cursor-pointer">
                        +1 more
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-16 flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
                <div className="size-10 rounded-full bg-muted/30 flex items-center justify-center text-muted-foreground/50">
                  <StickyNote className="w-5 h-5" />
                </div>
                <p className="text-xs italic">No activity recorded for this contact yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── RIGHT SIDE PANEL (Threads | Tasks | Notes | Files | Anchors | Meta) ───── */}
      <div className="w-full lg:w-[420px] xl:w-[460px] bg-card border-l border-border flex flex-col shrink-0 h-full">
        {/* Header Tab Bar */}
        <div className="p-2 border-b border-border bg-muted/20 shrink-0">
          <EntityFeatureBar
            items={featureBarItems}
            activeTab={currentTab}
            onSelectTab={(tab) => setActiveSubTab(tab as ContactSubTab)}
          />
        </div>

        {/* Tab Content Container */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0 space-y-4 scrollbar-thin">
          {/* ── TAB: JOBS (Job Service History) ────────────────────────────── */}
          {currentTab === 'jobs' && (
            <div className="space-y-4">
              {contact?.id ? (
                <LinkedJobsSection
                  contactId={contact.id}
                  isReadOnly={isReadOnly}
                  onAddJob={() => onAddTask?.()}
                />
              ) : (
                <div className="p-4 rounded-xl border border-dashed border-border bg-slate-50 dark:bg-slate-900/60 text-center space-y-2">
                  <Briefcase className="w-8 h-8 text-muted-foreground mx-auto" />
                  <p className="text-xs text-muted-foreground">
                    Select a contact to view their scheduled jobs and history.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: COMMENTS (Internal Discussion) ────────────────────────── */}
          {currentTab === 'comments' && (
            <div className="flex flex-col h-full space-y-3 min-h-0">
              <div className="flex items-center justify-between px-1 shrink-0">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Internal Comments
                </span>
              </div>

              {contact?.id ? (
                <ContactCommentThread contactId={contact.id} isReadOnly={isReadOnly} />
              ) : (
                <div className="p-6 rounded-xl border border-dashed text-center text-xs text-muted-foreground bg-slate-50 dark:bg-slate-900/60">
                  Select or save a contact to start an internal comment thread.
                </div>
              )}
            </div>
          )}

          {/* ── TAB 2: TASKS ─────────────────────────────────────────────────── */}
          {currentTab === 'tasks' && (
            <div className="space-y-4">
              {contact?.id ? (
                <TaskPanel
                  tasks={contactTasksQuery.data ?? []}
                  statuses={taskStatusesQuery.data?.items ?? []}
                  members={workspaceMembers}
                  isLoading={contactTasksQuery.isLoading}
                  isReadOnly={isReadOnly}
                  title="Tasks"
                  descriptionLines={1}
                  createContext={{ contactId: contact.id }}
                  createOpen={isCreateTaskOpen}
                  onCreateOpenChange={setIsCreateTaskOpen}
                  onCreated={() => contactTasksQuery.refetch()}
                  onTaskClick={(taskId) =>
                    orgId &&
                    workspaceId &&
                    navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${taskId}`)
                  }
                  onToggleComplete={(task) =>
                    toggleTaskMutation.mutate({
                      id: task.id,
                      status: toggleTaskStatusKey(taskStatusesQuery.data?.items ?? [], task.status),
                    })
                  }
                />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Tasks
                    </span>
                    <Button
                      size="sm"
                      onClick={handleAddTask}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-xs h-7 px-3 gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Task
                    </Button>
                  </div>
                  <div className="p-4 rounded-xl border border-dashed border-border bg-slate-50 dark:bg-slate-900/60 text-center space-y-2">
                    <ListTodo className="w-8 h-8 text-muted-foreground mx-auto" />
                    <p className="text-xs text-muted-foreground">
                      No open tasks linked to this contact yet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── TAB 3: NOTES ─────────────────────────────────────────────────── */}
          {currentTab === 'notes' && (
            <div className="flex flex-col h-full space-y-3 min-h-0">
              <div className="flex items-center justify-between px-1 shrink-0">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Contact Notes
                </span>
              </div>

              {contact?.id ? (
                <NotesPanel
                  entityType="contact"
                  entityId={contact.id}
                  isReadOnly={isReadOnly}
                  placeholder="Capture a useful detail about this contact…"
                  onChanged={() =>
                    utils.contacts.getActivityFeed.invalidate({ contactId: contact.id })
                  }
                  className="flex-1"
                />
              ) : (
                <div className="p-6 rounded-xl border border-dashed text-center text-xs text-muted-foreground bg-slate-50 dark:bg-slate-900/60">
                  Select or save a contact to add notes.
                </div>
              )}
            </div>
          )}

          {/* ── TAB 4: FILES ─────────────────────────────────────────────────── */}
          {currentTab === 'files' && (
            <div className="flex flex-col h-full space-y-3 min-h-0">
              <div className="flex items-center justify-between px-1 shrink-0">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Files & Documents
                </span>
              </div>

              {contact?.id ? (
                <ContactFilesTab contactId={contact.id} isReadOnly={isReadOnly} />
              ) : (
                <div className="p-6 rounded-xl border border-dashed text-center text-xs text-muted-foreground bg-slate-50 dark:bg-slate-900/60">
                  Select or save a contact to view and upload files.
                </div>
              )}
            </div>
          )}

          {/* ── TAB 5: ANCHORS ───────────────────────────────────────────────── */}
          {currentTab === 'anchors' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Connected Anchors
                </span>
                {!isReadOnly && onAddAnchor && (
                  <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={onAddAnchor}>
                    <Plus className="h-3.5 w-3.5" />
                    Add anchor
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {contact?.anchors && contact.anchors.length > 0 ? (
                  contact.anchors.map((anchor) => (
                    <a
                      key={anchor.id}
                      href={anchor.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-xl border border-border bg-slate-50 dark:bg-slate-900/60 flex items-center justify-between hover:border-primary/40 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="p-2 rounded-lg bg-primary/10 text-primary border border-primary/20">
                          <Globe className="w-4 h-4" />
                        </div>
                        <div className="truncate">
                          <p className="text-xs font-semibold text-foreground truncate">
                            {anchor.label || anchor.domain}
                          </p>
                          <p className="text-[10px] text-muted-foreground truncate">{anchor.url}</p>
                        </div>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    </a>
                  ))
                ) : (
                  <div className="rounded-xl border border-dashed border-border bg-slate-50 p-5 text-center dark:bg-slate-900/60">
                    <AnchorIcon className="mx-auto h-7 w-7 text-muted-foreground/60" />
                    <p className="mt-2 text-xs font-semibold text-foreground">
                      No anchors connected
                    </p>
                    <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                      Connect a page to make this contact available from that URL.
                    </p>
                    {!isReadOnly && onAddAnchor && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-3 h-7 text-xs"
                        onClick={onAddAnchor}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Add first anchor
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── TAB 6: META ──────────────────────────────────────────────────── */}
          {currentTab === 'meta' && (
            <div className="space-y-4">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Metadata & Properties
              </span>

              {/* Tags Section using CATALOG's TagChips */}
              {contact?.tags && contact.tags.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <TagIcon className="w-3 h-3" /> Tags
                  </span>
                  <TagChips tags={contact.tags} />
                </div>
              )}

              {/* Address Section using CATALOG's AddressBlock */}
              {(contact?.addressStreet ||
                contact?.addressCity ||
                contact?.addressState ||
                contact?.addressPostalCode) && (
                <div className="space-y-1.5">
                  <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Mailing Address
                  </span>
                  <div className="p-3 rounded-xl border border-border bg-slate-50 dark:bg-slate-900/60">
                    <AddressBlock
                      street={contact.addressStreet}
                      city={contact.addressCity}
                      state={contact.addressState}
                      postalCode={contact.addressPostalCode}
                      country={contact.addressCountry}
                    />
                  </div>
                </div>
              )}

              {/* General Properties Table */}
              <div className="bg-slate-50 dark:bg-slate-900/60 border border-border rounded-xl divide-y divide-border">
                <div className="flex items-center justify-between p-3 text-xs">
                  <span className="text-muted-foreground font-medium">Type</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                    {contact?.contactType?.label || 'General'}
                  </Badge>
                </div>

                {contact?.owner && (
                  <div className="flex items-center justify-between p-3 text-xs">
                    <span className="text-muted-foreground font-medium">Owner</span>
                    <span className="font-semibold text-foreground">
                      {contact.owner.fullName || contact.owner.email}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between p-3 text-xs">
                  <span className="text-muted-foreground font-medium">Company Name</span>
                  <span className="font-semibold text-foreground">{companyName}</span>
                </div>

                <div className="flex items-center justify-between p-3 text-xs">
                  <span className="text-muted-foreground font-medium">Primary Email</span>
                  <span className="font-semibold text-foreground">{primaryEmail}</span>
                </div>
              </div>

              {/* Custom Attributes using ContactCustomFields */}
              {contact && (
                <ContactCustomFields contact={contact} variant="readonly" isReadOnly={isReadOnly} />
              )}

              {contact?.notes && (
                <div className="space-y-2 pt-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                    <StickyNote className="w-3 h-3" /> Profile note
                  </span>
                  <RichTextContent
                    className="p-3 rounded-xl border border-border bg-slate-50 dark:bg-slate-900/60 text-xs text-foreground leading-relaxed break-words"
                    html={contact.notes}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
